import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react/dist/csr/ClockCounterClockwise';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { SwapIcon } from '@phosphor-icons/react/dist/csr/Swap';

import { DeviceEventTimeline } from './DeviceEventTimeline';
import { DeviceFormDialog } from './DeviceFormDialog';
import { DeviceStatusDialog } from './DeviceStatusDialog';
import { PageActions } from '../../app/shell/PageActions';
import { Button } from '../../design-system/primitives/Button/Button';
import { ApiError } from '../../lib/api/client';
import { readersApi, sensorsApi, type Reader, type Sensor } from '../../lib/api/devices';
import styles from './DeviceDetail.module.css';

/**
 * The device detail view — the record plus its event timeline, newest
 * first (`DeviceEventTimeline`'s own docblock).
 *
 * `kind` decides which API this reaches (`sensorsApi.show`/`readersApi.show`)
 * — the same asymmetry every other file in this module carries rather than
 * smoothing over. `DeviceScreen` owns the "which device is open" state and
 * hands this component `kind`+`uuid`; this component owns the fetch, the
 * edit dialog and the status dialog for exactly that one record.
 */

export interface DeviceDetailProps {
  kind: 'sensor' | 'reader';
  uuid: string;
  permissions: readonly string[];
  onBack: () => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function DeviceDetail({ kind, uuid, permissions, onBack }: DeviceDetailProps) {
  const { t, i18n } = useTranslation();
  const canManage = permissions.includes('device.manage');
  // Mirrored under `dir="rtl"` by CSS, as Pagination's carets are.
  const backIcon = <ArrowLeftIcon size={16} className={styles['backIcon']} />;

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [device, setDevice] = useState<Sensor | Reader | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const record = kind === 'sensor' ? await sensorsApi.show(uuid) : await readersApi.show(uuid);
      setDevice(record);
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setFailureMessage(
        error.code === 'not_found' ? t('devices.notFound') : t('devices.loadFailedDetail'),
      );
      setStatus('error');
    }
  }, [kind, uuid, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (status === 'loading') {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={backIcon}>
          {t('devices.back')}
        </Button>
        <p role="status">{t('list.loading')}</p>
      </div>
    );
  }

  if (status === 'error' || device === null) {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={backIcon}>
          {t('devices.back')}
        </Button>
        <div role="alert" className={styles['recovery']}>
          <p>{failureMessage}</p>
          <Button
            variant="secondary"
            icon={<ArrowClockwiseIcon size={16} />}
            onClick={() => {
              void load();
            }}
          >
            {t('devices.retry')}
          </Button>
          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="deviceDetailRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      </div>
    );
  }

  const typeLabel =
    (i18n.language.startsWith('ar') ? device.type_label_ar : device.type_label_en) ?? device.type;

  return (
    <div className={styles['page']}>
      {/* Back and the serial share one row; the record's commands go up into
          the shell's title row with every other screen's. */}
      <div className={styles['identity']}>
        <Button variant="ghost" onClick={onBack} icon={backIcon}>
          {t('devices.back')}
        </Button>
        <h2 className={styles['title']}>
          <bdi dir="ltr">{device.serial_number}</bdi>
        </h2>
      </div>

      {canManage ? (
        <PageActions>
          <Button
            variant="secondary"
            icon={<PencilSimpleIcon size={16} />}
            onClick={() => {
              setFormOpen(true);
            }}
          >
            {t('devices.edit')}
          </Button>
          <Button
            icon={<SwapIcon size={16} />}
            onClick={() => {
              setStatusDialogOpen(true);
            }}
          >
            {t('devices.changeStatus')}
          </Button>
        </PageActions>
      ) : null}

      <dl className={[styles['panel'], styles['primary'], styles['fields']].join(' ')}>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('devices.fieldType')}</dt>
          <dd className={styles['fieldValue']}>{typeLabel}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('devices.fieldStatus')}</dt>
          <dd className={styles['fieldValue']}>{t(`devices.status.${device.status}`)}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('devices.fieldDispensary')}</dt>
          <dd className={styles['fieldValue']}>
            {device.dispensary?.name ?? t('table.notRecorded')}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('devices.fieldNotes')}</dt>
          <dd className={styles['fieldValue']}>{device.notes ?? t('table.notRecorded')}</dd>
        </div>
      </dl>

      <section className={[styles['panel'], styles['section']].join(' ')}>
        <CardHead level="h3" icon={<ClockCounterClockwiseIcon size={16} weight="duotone" />} title={t('devices.timelineTitle')} />
        <DeviceEventTimeline events={device.events ?? []} />
      </section>

      <DeviceFormDialog
        kind={kind}
        open={formOpen}
        node={device}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={(saved) => {
          setDevice(saved);
          setFormOpen(false);
        }}
      />

      <DeviceStatusDialog
        kind={kind}
        open={statusDialogOpen}
        device={device}
        onClose={() => {
          setStatusDialogOpen(false);
        }}
        onChanged={() => {
          // NOT `setDevice(updated)` then `load()` — that assignment was
          // dead code (review finding 4): `load()` fires straight after and
          // its response is what actually paints, so the resource
          // `setStatus` itself returned was thrown away unread the instant
          // this ran. `load()` alone is kept, not the assignment, because
          // it is the one call that also re-fetches `events` — the new
          // `device_events` row this status change just wrote — which the
          // status response's own resource may not carry (`events` is
          // `whenLoaded()`-gated to the detail endpoint; `devices.ts`'s own
          // docblock).
          setStatusDialogOpen(false);
          void load();
        }}
      />
    </div>
  );
}
